// src/pages/modules/UpdateWalletPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchWallets,
  creditWallet,
  clearError,
  fetchWalletHistory,
  exportWalletHistory,
  exportWallets,
} from "store/slices/walletSlice";
import { usePermissions } from "hooks/usePermissions";
import usePermissionSocket from "hooks/usePermissionSocket";
import { Wallet, History } from "lucide-react";
import DataTable from "components/common/DataTable";
import PageHeader from "components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { Avatar, AvatarFallback } from "components/ui/avatar";
import { toast } from "sonner";
import { formatDateTime } from "utlis/formateDateTime";

const UpdateWalletPage = () => {
  usePermissionSocket();
  const dispatch = useDispatch();
  const { wallets, history, isLoading, error } = useSelector((s) => s.wallet);
  const { canRead, canUpdate, canExport } = usePermissions("employee_wallet");

  const [activeTab, setActiveTab] = useState("wallets");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // FIXED: Correctly extract global history (key = "global")
  const globalHistory = history.global || { items: [], pagination: { count: 0, page_size: 10 } };
  const allHistory = globalHistory.items;
  // const historyPagination = globalHistory.pagination;
  const historyPagination = globalHistory.pagination || { count: 0 };

  const walletPagination = useSelector((state) => state.wallet.pagination);

  // Refetch wallets
  const refetchWallets = useCallback(() => {
    dispatch(fetchWallets({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);

  // FIXED: Refetch ALL history with pagination + search
  const refetchHistory = useCallback(() => {
    dispatch(
      fetchWalletHistory({
        walletId: null,     // null = all wallets
        search: searchTerm,
        page,
        pageSize,
      })
    );
  }, [dispatch, searchTerm, page, pageSize]);

  // Re-fetch when tab, page, size, or search changes
  useEffect(() => {
    if (activeTab === "wallets") {
      refetchWallets();
    } else {
      refetchHistory();
    }
  }, [activeTab, page, pageSize, searchTerm, refetchWallets, refetchHistory]);

  // Error toast
  useEffect(() => {
    if (error) {
      toast.error(typeof error === "string" ? error : "An error occurred");
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const walletColumns = [
    {
      accessorKey: "employee_details.name",
      header: "Employee Code",
      cell: ({ row }) => (
        <div>
          <p className="text-sm">
            {row.original.employee_details?.code
              ? row.original.employee_details.code
              : '-'}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "employee_details.name",
      header: "Employee Name",
      cell: ({ row }) => (
        <div>
          <p className="font-medium">  {row.original.employee_details?.name
            ? row.original.employee_details.name
            : '-'}</p>
        </div>
      ),
    },
    {
      header: "Current Balance",
      cell: ({ row }) => (
        <div className="flex items-center gap-2 font-bold text-lg text-green-600">
          <Wallet className="h-5 w-5" />
          ₹{parseFloat(row.original.balance).toFixed(2)}
        </div>
      ),
    },
     {
          accessorKey: 'updated_at',
          header: 'Updated On',
          cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.updated_at) ?? '-'}</span>,
        },
  ];

  const historyColumns = [

    {
      header: "Employee Name",
      cell: ({ row }) => {
        const emp = row.original?.wallet_details;
        return emp ? `${emp.name} (${emp.code})` : "-";
      },
    },
    {
      header: "Type",
      cell: ({ row }) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${row.original.transaction_type === "credit"
          ? "bg-green-100 text-green-800"
          : "bg-red-100 text-red-800"
          }`}>
          {row.original.transaction_type.toUpperCase()}
        </span>
      ),
    },
    {
      header: "Amount",
      cell: ({ row }) => <span className="font-semibold">₹{row.original.amount}</span>,
    },
    {
      header: "Balance After",
      cell: ({ row }) => <span className="font-medium">₹{row.original.balance_after}</span>,
    },
    {
      accessorKey: "created_at",
      header: "Transaction Time",
      cell: ({ row }) => new Date(row.original.created_at).toLocaleString("en-IN"),
    },
    {
      accessorKey: "created_by_name",
      header: "Last Updated By",
      cell: ({ row }) => row.original.created_by_name || "-",
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Wallet Management"
        description="View all employee wallets and complete transaction history"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="wallets">
            <Wallet className="h-4 w-4 mr-2" />
            Wallets
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="h-4 w-4 mr-2" />
            All History
          </TabsTrigger>
        </TabsList>

        {/* Wallets Tab */}
        <TabsContent value="wallets" className="mt-6">
          {/* {canRead ? ( */}
          <DataTable
            data={wallets}
            columns={walletColumns}
            totalCount={walletPagination.count}
            searchPlaceholder="Search employee name or code..."
            pageIndex={page - 1}
            pageSize={pageSize}
            // pageCount={Math.ceil(wallets.length / pageSize)}
            onPageChange={(p) => setPage(p + 1)}
            pageCount={walletPagination.total_pages || Math.ceil(walletPagination.count / pageSize) || 1}
            onPageSizeChange={setPageSize}
            onSearch={setSearchTerm}
            isLoading={isLoading}
            showExport={true}
            showImport={false}
            onExport={canExport ? () => dispatch(exportWallets()) : undefined}
          //  onDelete={async (id) => {
          //             await dispatch(deleteRole(id));
          //             refetch();
          //           }}
          //           onRestore={async (id) => {
          //             await dispatch(updateRole({ id, is_deleted: false }));
          //             refetch();
          //           }}
          />
          {/* ) */}
          {/* //  : (
          //   <p className="text-center py-12 text-muted-foreground">
          //     You don't have permission to view wallets.
          //   </p>
          // )} */}
        </TabsContent>

        {/* All History Tab — NOW FULLY WORKING */}
        <TabsContent value="history" className="mt-6">
          <DataTable
            data={allHistory}
            totalCount={historyPagination.count}
            columns={historyColumns}
            searchPlaceholder="Search by employee, amount, date..."
            isLoading={isLoading}
            pageIndex={page - 1}
            pageSize={pageSize}
            pageCount={Math.ceil(historyPagination.count / pageSize)}
            onPageChange={(p) => setPage(p + 1)}
            onPageSizeChange={setPageSize}
            onSearch={setSearchTerm}
            showExport={true}
            showImport={false}
            onExport={canExport ? () => dispatch(exportWalletHistory()) : undefined}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UpdateWalletPage;